import { Button, Checkbox } from "@mui/material";
import { useFormik, Field, FormikProvider } from "formik";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { ReactComponent as RightArrowIcon } from "assets/right-arrow.svg";
import { InputField } from "components/InputField";
import { passwordYupRule } from "constants/passwordValidation";
import { SLAPSHOT_HOME_URL, SLAPSHOT_LOGIN_URL } from "constants/urls";
import { resetPassword } from "services/LoginService";

import styles from "./SlapshotSetPassword.module.scss";
import { AUTH_TOKEN, AUTH_USER } from "../../constants/constants";

const SlapshotSetPasswordPage = () => {
  const { userIdentifier, token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [agreeToTermsAndConditions, setAgreeToTermsAndConditions] =
    useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: passwordYupRule,
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), ""], "Passwords must match")
        .required("Please retype you password."),
    }),
    onSubmit: async (values, { setFieldError }) => {
      setIsLoading(true);
      if (userIdentifier && token) {
        resetPassword(userIdentifier, token, values.password, true)
          .then((res) => {
            localStorage.setItem(AUTH_TOKEN, res.token);
            localStorage.setItem(AUTH_USER, JSON.stringify(res.data));
            navigate(SLAPSHOT_HOME_URL);
          })
          .catch((e) => {
            setFieldError(
              "password",
              e?.detail || "Could not set your password. Please try again.",
            );
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
  });

  const isDisabled = () => {
    return (
      isLoading ||
      !!formik.errors.password ||
      !!formik.errors.confirmPassword ||
      !agreeToTermsAndConditions
    );
  };

  return (
    <FormikProvider value={formik}>
      <form className={styles.formContainer} onSubmit={formik.handleSubmit}>
        <div className={styles.innerContainer}>
          <div className={styles.textContainer}>
            <h3>Set Password</h3>
            <p>
              Please set your password to complete
              <br />
              setting up your Slapshot account.
            </p>
          </div>
          <div className={styles.fieldRow}>
            <Field
              name="password"
              type="password"
              component={InputField}
              inputClassName={styles.input}
              label="Password"
              placeholder="* * * * * * * * *"
            />
            <Field
              name="confirmPassword"
              type="password"
              component={InputField}
              inputClassName={styles.input}
              label="Confirm Password"
              placeholder="* * * * * * * * *"
            />
          </div>
        </div>
        <div className={styles.termsContainer}>
          <Checkbox
            name="termsAndConditions"
            checked={agreeToTermsAndConditions}
            onChange={() => {
              setAgreeToTermsAndConditions(!agreeToTermsAndConditions);
            }}
            className={styles.checkbox}
          />
          <p>
            I agree to Slapshot's&nbsp;
            <a
              href="https://ssai-public-assets.s3.us-east-2.amazonaws.com/admin-public-media/agreements/Slapshot+-+Terms+and+Conditions+-+Oct+5th+2024.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Terms & Conditions
            </a>
          </p>
        </div>
        <Button
          type="submit"
          className="slapshotAuthButton"
          disabled={isDisabled()}
        >
          Set Password
        </Button>
      </form>
    </FormikProvider>
  );
};

export default SlapshotSetPasswordPage;
